export type Language = { code: string; name: string; native: string; flag: string };

export const LANGUAGES: Language[] = [
  { code: "en", name: "English",    native: "English",    flag: "🇬🇧" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt", flag: "🇻🇳" },
  { code: "el", name: "Greek",      native: "Ελληνικά",   flag: "🇬🇷" },
];

export const DEFAULT_LANGUAGE: Language = LANGUAGES[0];

export const languageByCode = (code: string): Language =>
  LANGUAGES.find((l) => l.code === code) ?? DEFAULT_LANGUAGE;

/**
 * Fallback metadata for common locale codes. Used when a new translation
 * locale appears in the DB that isn't in the curated LANGUAGES list — so
 * admins can ship a new language without a shop redeploy.
 */
const LANG_META_FALLBACK: Record<string, { name: string; native: string; flag: string }> = {
  en: { name: "English",     native: "English",     flag: "🇬🇧" },
  vi: { name: "Vietnamese",  native: "Tiếng Việt",  flag: "🇻🇳" },
  es: { name: "Spanish",     native: "Español",     flag: "🇪🇸" },
  fr: { name: "French",      native: "Français",    flag: "🇫🇷" },
  de: { name: "German",      native: "Deutsch",     flag: "🇩🇪" },
  it: { name: "Italian",     native: "Italiano",    flag: "🇮🇹" },
  pt: { name: "Portuguese",  native: "Português",   flag: "🇵🇹" },
  nl: { name: "Dutch",       native: "Nederlands",  flag: "🇳🇱" },
  pl: { name: "Polish",      native: "Polski",      flag: "🇵🇱" },
  ru: { name: "Russian",     native: "Русский",     flag: "🇷🇺" },
  uk: { name: "Ukrainian",   native: "Українська",  flag: "🇺🇦" },
  tr: { name: "Turkish",     native: "Türkçe",      flag: "🇹🇷" },
  ar: { name: "Arabic",      native: "العربية",     flag: "🇦🇪" },
  he: { name: "Hebrew",      native: "עברית",       flag: "🇮🇱" },
  hi: { name: "Hindi",       native: "हिन्दी",        flag: "🇮🇳" },
  zh: { name: "Chinese",     native: "中文",         flag: "🇨🇳" },
  ja: { name: "Japanese",    native: "日本語",        flag: "🇯🇵" },
  ko: { name: "Korean",      native: "한국어",        flag: "🇰🇷" },
  th: { name: "Thai",        native: "ไทย",         flag: "🇹🇭" },
  id: { name: "Indonesian",  native: "Bahasa Indonesia", flag: "🇮🇩" },
  ms: { name: "Malay",       native: "Bahasa Melayu", flag: "🇲🇾" },
  fil: { name: "Filipino",   native: "Filipino",    flag: "🇵🇭" },
  sv: { name: "Swedish",     native: "Svenska",     flag: "🇸🇪" },
  no: { name: "Norwegian",   native: "Norsk",       flag: "🇳🇴" },
  da: { name: "Danish",      native: "Dansk",       flag: "🇩🇰" },
  fi: { name: "Finnish",     native: "Suomi",       flag: "🇫🇮" },
  cs: { name: "Czech",       native: "Čeština",     flag: "🇨🇿" },
  el: { name: "Greek",       native: "Ελληνικά",    flag: "🇬🇷" },
  ro: { name: "Romanian",    native: "Română",      flag: "🇷🇴" },
  hu: { name: "Hungarian",   native: "Magyar",      flag: "🇭🇺" },
  bg: { name: "Bulgarian",   native: "Български",   flag: "🇧🇬" },
};

/**
 * Resolve display metadata for ANY locale code — curated first, common-fallback
 * second, generic last. Lets us render a new admin-added locale immediately.
 */
export function resolveLanguageMeta(code: string): Language {
  const curated = LANGUAGES.find((l) => l.code === code);
  if (curated) return curated;
  const fallback = LANG_META_FALLBACK[code];
  if (fallback) return { code, ...fallback };
  return { code, name: code.toUpperCase(), native: code.toUpperCase(), flag: "🌐" };
}